import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useGetWalletBalance } from '~entities/wallet';

import { KEYS } from '~shared/api/query-keys';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 90_000;

export interface WalletBalancePollingTarget {
  expectedBalance: number;
  startedAt: number;
}

const hasReachedExpectedBalance = (
  balance: number,
  expectedBalance: number,
): boolean =>
  Math.abs(balance - expectedBalance) < 0.01 || balance > expectedBalance;

const hasPollingTimedOut = (startedAt: number): boolean =>
  Date.now() - startedAt >= POLL_TIMEOUT_MS;

const isPollingComplete = (
  balance: number,
  pollingTarget: WalletBalancePollingTarget,
): boolean =>
  hasReachedExpectedBalance(balance, pollingTarget.expectedBalance) ||
  hasPollingTimedOut(pollingTarget.startedAt);

export const useWalletBalancePolling = (
  parentId: string,
  pollingTarget: WalletBalancePollingTarget | null,
  onComplete: () => void,
) => {
  const queryClient = useQueryClient();
  const { refetch } = useGetWalletBalance(parentId);

  useEffect(() => {
    if (!pollingTarget) {
      return;
    }

    let isActive = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextPoll = () => {
      timeoutId = setTimeout(() => {
        void pollWalletBalance();
      }, POLL_INTERVAL_MS);
    };

    const pollWalletBalance = async () => {
      try {
        const result = await refetch();
        if (!isActive) {
          return;
        }

        const balance = result.data?.walletBalance ?? 0;

        if (isPollingComplete(balance, pollingTarget)) {
          void queryClient.invalidateQueries({
            queryKey: [KEYS.PARENT_WALLET_TRANSACTIONS, parentId],
          });
          onComplete();
          return;
        }

        scheduleNextPoll();
      } catch {
        if (!isActive) {
          return;
        }
        scheduleNextPoll();
      }
    };

    void pollWalletBalance();

    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [pollingTarget, onComplete, parentId, queryClient, refetch]);
};
